/**
 * The "Pages consulted" footer under an assistant turn (NW-32).
 *
 * Registered as the styled Thread's `AssistantMessageExtra` slot, so it renders at the
 * bottom of the message it belongs to — beneath the answer, above the action bar.
 *
 * Everything it shows is DERIVED from the turn's `web-fetch` tool parts (see
 * ./consultedPages.ts), never from the answer text: attribution the model retypes from
 * memory can be wrong in ways a reader cannot check, and this list has to be the one thing
 * in the message that can't be. The model is still asked to cite inline, at the claim (see
 * the MCP server's EXTERNAL_CONTENT_RULE and the EXTERNAL SOURCE block it puts on every
 * result) — that answers "where did this number come from"; this footer answers "what did
 * you look at", which is a different question and the auditable one.
 *
 * Renders nothing — no heading, no rule — for the overwhelming majority of turns, which
 * fetch nothing at all.
 */
import { textDataAttr } from "@mdotm/shadcn/legacy/components";
import { useAuiState } from "@assistant-ui/react";
import { useMemo } from "react";
import { useLocaleFormatters } from "$root/localization/hooks";
import { collectConsultedPages, type ConsultedPage } from "./consultedPages";

export function SphereAIConsultedPages(): React.JSX.Element | null {
	const parts = useAuiState((s) => s.message.parts);
	// Withheld until the turn settles. The execution panel already narrates each fetch as it
	// happens, so a half-built list of sources during the run would say the same thing twice
	// — and "Pages consulted" reads as a summary, which mid-stream it isn't yet.
	const isRunning = useAuiState((s) => s.message.status?.type === "running");
	// Only pages that actually came back. A fetch the site refused says nothing about where the
	// answer came from, which is all this footer is for; the execution panel already narrates
	// the failed attempt for anyone asking why the answer is thin.
	const pages = useMemo(() => collectConsultedPages(parts).filter((page) => !page.unread), [parts]);

	if (isRunning || pages.length === 0) return null;

	return (
		<section
			data-qualifier="SphereAI/Message/ConsultedPages"
			className="border-border mt-3 border-t pt-2"
			{...textDataAttr("Body/S/Book")}
		>
			<h3 className="text-muted-foreground">Pages consulted</h3>
			<ul className="mt-1 flex flex-col gap-0.5">
				{pages.map((page) => (
					<li key={page.url} className="min-w-0">
						<ConsultedPageRow page={page} />
					</li>
				))}
			</ul>
		</section>
	);
}

/**
 * One row. The label is the URL shortened for reading (`example.com/a/b`) and the full URL
 * rides on the title, so a long link can't push the row wide — it truncates.
 *
 * Every row here is a page that was read — unread attempts are filtered out upstream — so a
 * row means "this is a source of the answer", with no second state to read for.
 */
function ConsultedPageRow({ page }: { page: ConsultedPage }): React.JSX.Element {
	const { formatDate, formatTime } = useLocaleFormatters();

	const retrieved = page.retrievedAt ? new Date(page.retrievedAt) : null;
	const retrievedLabel =
		retrieved && !Number.isNaN(retrieved.getTime())
			? `retrieved ${formatDate(retrieved)} ${formatTime(retrieved)}`
			: null;
	const title = [page.url, retrievedLabel].filter(Boolean).join(" · ");

	// Not a link when the URL isn't http(s) — see isLinkableUrl. `noreferrer` and `nofollow`
	// because every one of these is an external page the model chose, not a site we vouch for.
	return (
		<span data-qualifier="SphereAI/Message/ConsultedPage" className="flex min-w-0 items-baseline gap-1.5">
			{page.linkable ? (
				<a
					href={page.url}
					title={title}
					target="_blank"
					rel="noreferrer nofollow"
					className="text-muted-foreground hover:text-foreground min-w-0 underline decoration-dotted underline-offset-2"
				>
					<span className="block truncate">{page.label}</span>
				</a>
			) : (
				<span title={title} className="text-muted-foreground block min-w-0 truncate">
					{page.label}
				</span>
			)}
		</span>
	);
}
